import { useState } from "react";
import type { z } from "zod";
import { siteActivityResponse } from "@sitebrew/contracts/v1";
import { ApiError } from "@/lib/api";
import { useDiscardChange, usePublishChange, useSiteActivity } from "@/lib/site-activity";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Notice } from "@/components/ui/login-card";

type Activity = z.infer<typeof siteActivityResponse>;
type JobCard = Activity["jobs"][number];
type ChangeCard = Activity["changes"][number];

const when = (value: number | string) => new Date(value).toLocaleString(undefined, {
  dateStyle: "medium", timeStyle: "short",
});

/** Only an HTTPS preview recorded for this change may become a browser link. */
function previewHref(url: string | undefined): string | null {
  if (!url) return null;
  try { return new URL(url).protocol === "https:" ? url : null; }
  catch { return null; }
}

function ChangeRow({ siteId, item }: { siteId: string; item: ChangeCard }) {
  const { change } = item;
  const publish = usePublishChange(siteId);
  const discard = useDiscardChange(siteId);
  const [confirming, setConfirming] = useState(false);
  const preview = previewHref(change.previewUrl);
  const discardable = item.discardable;
  const publishable = item.publishable;
  const failure = publish.error ?? discard.error;

  return <li className="rounded-lg border border-border p-4">
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary">{change.state.replaceAll("_", " ")}</Badge>
      <span className="text-sm text-muted-foreground">Updated {when(change.checkedAt)}</span>
    </div>
    {change.checksState === "failed" ? <p className="mt-2 text-sm text-destructive">The preview build failed. Review the change before publishing.</p> : null}
    {discardable && !publishable && (change.checksState === undefined || change.checksState === "pending") ?
      <p className="mt-2 text-sm text-muted-foreground">Checks pending. Publishing will be available after the preview build passes.</p> : null}
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {preview ? <a href={preview} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline">Open preview ↗</a> :
        <span className="text-sm text-muted-foreground">No preview is available for this change yet.</span>}
      {publishable && !confirming ? <Button size="sm" onClick={() => setConfirming(true)}>Publish</Button> : null}
      {discardable && !confirming ? <Button size="sm" variant="outline" disabled={discard.isPending}
        onClick={() => discard.mutate(change.changeId)}>Discard</Button> : null}
    </div>
    {confirming ? <div className="mt-3 rounded-md border border-border p-3">
      <p className="mb-2 text-sm">Publish this preview to the site's live address?</p>
      <div className="flex gap-2">
        <Button size="sm" disabled={publish.isPending} onClick={() => publish.mutate({ changeId: change.changeId, headCommit: change.headCommit }, { onSuccess: () => setConfirming(false) })}>
          {publish.isPending ? "Publishing…" : "Confirm publish"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>Cancel</Button>
      </div>
    </div> : null}
    {failure ? <p role="alert" className="mt-2 text-sm text-destructive">{failure instanceof ApiError ? failure.message : "The change could not be updated. Refresh and try again."}</p> : null}
  </li>;
}

function JobRow({ card }: { card: JobCard }) {
  const { job, request } = card;
  const events = [...(job.timeline ?? [])].sort((a, b) => a.seq - b.seq);
  const ending = job.explanation ?? job.note ?? (job.state === "cancelled" ? "This job was cancelled." :
    job.state === "expired" ? "This job reached its time or budget limit." : null);

  return <li className="relative border-l-2 border-border pb-6 pl-5 last:pb-0">
    <span className="absolute -left-[.42rem] top-1 h-3 w-3 rounded-full bg-primary" aria-hidden="true" />
    <div className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{job.state.replaceAll("_", " ")}</Badge>
        <span className="text-sm text-muted-foreground">{job.kind.replace("site_", "")}</span>
        <time className="text-sm text-muted-foreground" dateTime={new Date(job.createdAt).toISOString()}>{when(job.createdAt)}</time>
      </div>
      <h4 className="mt-2 font-medium">{request}</h4>
      {!job.terminal && job.current_activity ? <p className="mt-2 text-sm">Now: {job.current_activity}</p> : null}
      {ending ? <p className={job.state === "failed" ? "mt-2 text-sm text-destructive" : "mt-2 text-sm text-muted-foreground"}>{ending}</p> : null}
      {job.state === "failed" && !ending ? <p className="mt-2 text-sm text-destructive">This job failed. Refresh for more detail or try the request again.</p> : null}
      <details className="mt-3" open={!job.terminal}>
        <summary className="cursor-pointer text-sm text-primary">Timeline ({events.length === 50 ? "latest 50" : events.length})</summary>
        {events.length ? <ol className="mt-2 space-y-2" aria-label={`Timeline for ${request}`}>
          {events.map((event) => <li key={event.seq} className="text-sm">
            <time className="mr-2 text-muted-foreground" dateTime={event.ts}>{when(event.ts)}</time>
            {event.level === "system" ? <span className="mr-2 rounded bg-muted px-1 font-mono text-xs">{event.kind}</span> : null}
            <span className={event.level === "agent" ? "font-medium" : "text-muted-foreground"}>{event.text}</span>
          </li>)}
        </ol> : <p className="mt-2 text-sm text-muted-foreground">No progress has been recorded yet.</p>}
      </details>
    </div>
  </li>;
}

export function SiteActivity({ siteId }: { siteId: string }) {
  const activity = useSiteActivity(siteId);
  const jobs = [...(activity.data?.jobs ?? [])].sort((a, b) =>
    b.job.createdAt - a.job.createdAt || b.job.jobId.localeCompare(a.job.jobId));

  return <section aria-labelledby="site-activity-title" className="mt-8">
    <h2 id="site-activity-title" className="mb-4 text-xl font-semibold">Activity</h2>
    {activity.isPending ? <p>Loading activity…</p> : null}
    {activity.error ? <Notice role="alert" className="mb-4">{activity.error instanceof ApiError ? activity.error.message : "Activity could not be loaded."} <Button size="sm" variant="outline" onClick={() => void activity.refetch()}>Try again</Button></Notice> : null}
    {activity.data ? <>
      {activity.data.changes.length ? <div className="mb-6">
        <h3 className="mb-2 font-medium">Changes</h3>
        <ul className="space-y-3">{activity.data.changes.map((item) => <ChangeRow key={item.change.changeId} siteId={siteId} item={item} />)}</ul>
      </div> : null}
      {jobs.length ? <>
        <h3 className="mb-3 font-medium">Jobs</h3>
        <ol>{jobs.map((card) => <JobRow key={card.job.jobId} card={card} />)}</ol>
      </> : <p className="text-sm text-muted-foreground">{activity.data.changes.length
        ? "No recent jobs. Older unreleased changes are shown above."
        : "No jobs yet. Changes requested for this site will appear here with their progress and preview."}</p>}
    </> : null}
  </section>;
}
